import React, {ChangeEvent, useEffect, useState} from "react";
import {Box, Grid, IconButton, InputAdornment, TextField} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import styled from "styled-components";
import {parseError} from "../../../../errors/http.service";
import {useNavigate} from "react-router-dom";
import {useSnackbar} from "../../../../contexts/SnackbarContext";
import {ActionButton, CancelButton} from "../../../../components/common/ActionButtons";
import {
    validatePassword as validatePasswordUtil,
    validatePasswordMatchWhenConfirmationPresent
} from "../../../../utils/password-validation";
import {ButtonGroup} from "../../UserSettings.styled";
import {useTranslation} from 'react-i18next';
import FieldHelpSuffix from "../../../../components/common/FieldHelpSuffix";
import {ValidationError} from "../../../../utils/validation";
import {authService} from "../../../../services/auth.service";

const StyledTextField = styled(TextField)`
    width: 100%;
    max-width: 550px;
`;

const PasswordSecurity = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    const [currentPassword, setCurrentPassword] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");

    const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);

    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [currentPasswordTouched, setCurrentPasswordTouched] = useState(false);
    const [newPasswordTouched, setNewPasswordTouched] = useState(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

    const [submitted, setSubmitted] = useState(false);

    const [currentPasswordServerError, setCurrentPasswordServerError] = useState<ValidationError | null>(null);
    const [newPasswordServerError, setNewPasswordServerError] = useState<ValidationError | null>(null);
    const [confirmPasswordServerError, setConfirmPasswordServerError] = useState<ValidationError | null>(null);

    const [isFormChanged, setIsFormChanged] = useState<boolean>(false);

    const currentPasswordValidationError: ValidationError | null = newPassword && !currentPassword
        ? {key: 'passwordSecurity.currentPasswordRequired'}
        : null;

    const newPasswordValidationError = newPassword ? validatePasswordUtil(newPassword) : null;

    const confirmPasswordValidationError = validatePasswordMatchWhenConfirmationPresent(
        newPassword,
        confirmPassword,
        'passwordSecurity.passwordsNotMatch'
    );
    const currentPasswordError = currentPasswordServerError ??
        (((currentPasswordTouched || submitted) && currentPasswordValidationError) ? currentPasswordValidationError : null);

    const newPasswordError = newPasswordServerError ??
        (((newPasswordTouched || submitted) && newPasswordValidationError) ? newPasswordValidationError : null);

    const confirmPasswordError = confirmPasswordServerError ??
        (((confirmPasswordTouched || submitted) && confirmPasswordValidationError) ? confirmPasswordValidationError : null);

    const isPasswordChangeReady = Boolean(
        currentPassword &&
        newPassword &&
        confirmPassword &&
        newPassword === confirmPassword
    );

    useEffect(() => {
        setIsFormChanged(isPasswordChangeReady);
    }, [isPasswordChangeReady]);

    const clearPasswordServerErrors = () => {
        setCurrentPasswordServerError(null);
        setNewPasswordServerError(null);
        setConfirmPasswordServerError(null);
    };

    const handleCurrentPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
        setCurrentPassword(e.target.value);
        clearPasswordServerErrors();
    };

    const handleNewPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
        setNewPassword(e.target.value);
        clearPasswordServerErrors();
    };

    const handleConfirmPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
        clearPasswordServerErrors();
    };

    const handleCancelPasswordChanges = () => {
        navigate(-1);
    };

    const handleSaveChanges = async () => {
        setSubmitted(true);
        clearPasswordServerErrors();

        if (currentPasswordValidationError || newPasswordValidationError || confirmPasswordValidationError) {
            return;
        }

        if (isPasswordChangeReady) {
            try {
                await authService.updateCurrentUserPassword(currentPassword, newPassword);

                showSnackbar(t('passwordSecurity.passwordChanged'), "success");

                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
                setIsFormChanged(false);
            } catch (passwordError: any) {
                const parsedError = parseError(passwordError);
                showSnackbar(parsedError, "error");

                if (passwordError.response && passwordError.response.status === 400) {
                    if (passwordError.response.data && passwordError.response.data.message) {
                        const errorMessage = passwordError.response.data.message;
                        if (errorMessage.includes("current password")) {
                            setCurrentPasswordServerError({key: 'passwordSecurity.invalidCurrentPassword'});
                        } else if (errorMessage.includes("new password")) {
                            setNewPasswordServerError({key: 'passwordSecurity.invalidNewPassword'});
                        }
                    }
                }

                return;
            }
        }
    };

    return (
        <Box sx={{
            paddingTop: { xs: '24px', md: '48px' },
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: {xs: 'column', md: 'row'},
                        gap: 3,
                        height: {md: '80px'}
                    }}>
                        <Box sx={{flex: 1}}>
                            <StyledTextField
                                label={t('passwordSecurity.currentPassword')}
                                type={showCurrentPassword ? "text" : "password"}
                                value={currentPassword}
                                onChange={handleCurrentPasswordChange}
                                onBlur={() => setCurrentPasswordTouched(true)}
                                fullWidth
                                variant="outlined"
                                error={!!currentPasswordError}
                                helperText={currentPasswordError ? String(t(currentPasswordError.key)) : ""}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconButton
                                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                    edge="end"
                                                    sx={{color: 'primary.main'}}
                                                >
                                                    {showCurrentPassword ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                                <FieldHelpSuffix
                                                    title={t('passwordSecurity.help.currentPassword')}
                                                    ariaLabel={t('passwordSecurity.help.currentPassword') as string}
                                                />
                                            </Box>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box sx={{flex: 1}}>
                            <StyledTextField
                                label={t('passwordSecurity.newPassword')}
                                type={showNewPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={handleNewPasswordChange}
                                onBlur={() => setNewPasswordTouched(true)}
                                fullWidth
                                variant="outlined"
                                error={!!newPasswordError}
                                helperText={newPasswordError ? String(t(newPasswordError.key)) : ""}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconButton
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    edge="end"
                                                    sx={{color: 'primary.main'}}
                                                >
                                                    {showNewPassword ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                                <FieldHelpSuffix
                                                    title={t('passwordSecurity.help.newPassword')}
                                                    ariaLabel={t('passwordSecurity.help.newPassword') as string}
                                                />
                                            </Box>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box sx={{flex: 1}}>
                            <StyledTextField
                                label={t('passwordSecurity.confirmPassword')}
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={handleConfirmPasswordChange}
                                onBlur={() => setConfirmPasswordTouched(true)}
                                fullWidth
                                variant="outlined"
                                error={!!confirmPasswordError}
                                helperText={confirmPasswordError ? String(t(confirmPasswordError.key)) : ""}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                    sx={{color: 'primary.main'}}
                                                >
                                                    {showConfirmPassword ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>

                                                <FieldHelpSuffix
                                                    title={t('passwordSecurity.help.confirmPassword')}
                                                    ariaLabel={t('passwordSecurity.help.confirmPassword') as string}
                                                />
                                            </Box>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <ButtonGroup>
                <CancelButton onClick={handleCancelPasswordChanges}>
                    {t('common.cancel')}
                </CancelButton>

                <ActionButton
                    onClick={handleSaveChanges}
                    disabled={!isFormChanged}
                >
                    {t('common.saveChanges')}
                </ActionButton>
            </ButtonGroup>
        </Box>
    );
};

export default PasswordSecurity;
